import React, { useCallback, useEffect, useRef, useState } from "react";
import Badge from "@mui/material/Badge";
import Popover from "@mui/material/Popover";
import { FiBell } from "react-icons/fi";
import AppTooltip from "../Common Components/AppTooltip";
import { showErrorToast } from "../../constants/ToastService.js";
import { sprintNotificationsApi } from "./sprintNotificationsApi";
import NotificationRow from "./NotificationRow";
import SprintNotificationHistory from "./SprintNotificationHistory";
import "./SprintNotifications.css";

/**
 * Bell in the sprint header: unread badge, a dropdown with the latest 10, and
 * the full history drawer. The count itself comes from the page's poll
 * (useSprintNotificationPoll) so it keeps updating while a ticket is open.
 */
const SprintNotificationBell = ({ sprint, count, onChanged, onOpenNotification }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [tab, setTab] = useState("unread");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [ring, setRing] = useState(false);
    const prevCount = useRef(count);

    // Shake the bell once when the count goes up.
    useEffect(() => {
        if (count > prevCount.current) {
            setRing(true);
            const t = setTimeout(() => setRing(false), 1000);
            prevCount.current = count;
            return () => clearTimeout(t);
        }
        prevCount.current = count;
        return undefined;
    }, [count]);

    // Which sprint + tab the list is for; an answer for an older one is dropped.
    const listKey = `${sprint?.id}|${tab}`;
    const currentKey = useRef(listKey);
    currentKey.current = listKey;

    // Another sprint or tab starts empty rather than showing the old rows.
    useEffect(() => {
        setItems([]);
    }, [listKey]);

    const load = useCallback(async () => {
        if (!sprint?.id) return;
        const key = `${sprint.id}|${tab}`;
        setLoading(true);
        try {
            const res = await sprintNotificationsApi.getList(sprint.id, { page: 1, page_size: 10, unread: tab === "unread" ? 1 : "" });
            if (currentKey.current !== key) return;
            if (res.status === "success") setItems(res.data.results);
            else showErrorToast(res.message);
        } catch (e) {
            if (currentKey.current === key) showErrorToast("Could not load notifications");
        } finally {
            if (currentKey.current === key) setLoading(false);
        }
    }, [sprint?.id, tab]);

    // Reload while open when the tab changes or a poll brings a new count.
    useEffect(() => {
        if (anchorEl) load();
    }, [anchorEl, load, count]);

    const handleMarkAll = async () => {
        try {
            await sprintNotificationsApi.markAllRead(sprint.id);
            onChanged();
            load();
        } catch (e) {
            showErrorToast(e.message);
        }
    };

    const handleRowClick = (item) => {
        setAnchorEl(null);
        onOpenNotification(item);
    };

    return (
        <>
            <AppTooltip title="Notifications for this sprint">
                <span style={{ display: "inline-flex" }}>
                    <button
                        className={`sl2-edit-sprint-btn sn-bell-btn${ring ? " sn-bell-btn--ring" : ""}`}
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        aria-label={count ? `Notifications, ${count} unread` : "Notifications"}
                    >
                        <Badge badgeContent={count} color="error" max={9} invisible={!count} overlap="circular">
                            <FiBell size={14} />
                        </Badge>
                    </button>
                </span>
            </AppTooltip>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{ paper: { className: "sn-pop" } }}
            >
                <div className="sn-pop__head">
                    <div>
                        <div className="sn-pop__title">Notifications</div>
                        <div className="sn-pop__sub">{sprint?.sprint_name}</div>
                    </div>
                    {count > 0 && <button type="button" className="sn-link" onClick={handleMarkAll}>Mark all as read</button>}
                </div>
                <div className="sn-tabs" role="tablist">
                    <button type="button" role="tab" aria-selected={tab === "unread"} className={`sn-tab${tab === "unread" ? " sn-tab--on" : ""}`} onClick={() => setTab("unread")}>
                        Unread{count ? ` (${count})` : ""}
                    </button>
                    <button type="button" role="tab" aria-selected={tab === "all"} className={`sn-tab${tab === "all" ? " sn-tab--on" : ""}`} onClick={() => setTab("all")}>
                        All
                    </button>
                </div>

                <div className="sn-pop__list">
                    {loading && !items.length && <div className="sn-empty__sub sn-pad">Loading…</div>}
                    {!loading && !items.length && (
                        <div className="sn-empty">
                            <FiBell size={22} />
                            <div className="sn-empty__title">You're all caught up</div>
                            <div className="sn-empty__sub">{tab === "unread" ? "No new updates in this sprint." : "No notifications in this sprint yet."}</div>
                        </div>
                    )}
                    {items.map(item => <NotificationRow key={item.id} item={item} onClick={handleRowClick} />)}
                </div>

                <button type="button" className="sn-pop__foot" onClick={() => { setAnchorEl(null); setHistoryOpen(true); }}>
                    View all history →
                </button>
            </Popover>

            <SprintNotificationHistory
                open={historyOpen}
                sprint={sprint}
                count={count}
                onClose={() => setHistoryOpen(false)}
                onChanged={onChanged}
                onOpenNotification={(item) => { setHistoryOpen(false); onOpenNotification(item); }}
            />
        </>
    );
};

export default SprintNotificationBell;
